import React, { useEffect, useState } from 'react';
import { Briefcase, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const STATE = { scheduled: 'Moves at renewal', switched: 'On Pay as you go', subscription_not_found: 'Moves at renewal (no Stripe sub)', cancel_failed: 'Stripe cancel failed — check' };

const Panel = ({ icon: Icon, title, sub, children, testid }) => (
  <section className="glass-card rounded-xl p-5 mb-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1"><Icon size={16} className="text-teal-400" /><h3 className="text-base font-semibold text-white">{title}</h3></div>
    <p className="text-xs text-gray-400 mb-4">{sub}</p>
    <div className="overflow-x-auto">{children}</div>
  </section>
);

export const PaygTransitionPanel = () => {
  const { api } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/api/admin/payg-transition').then(r => setRows(r.data.rows)).catch(() => setRows([])); }, [api]);
  if (!rows) return null;
  return (
    <Panel icon={ArrowRightLeft} title="Pay as you go transition" sub="Starter / Agency / Pro subscriptions are set to cancel at period end and switch to Pay as you go automatically on their renewal date. Lifetime accounts are untouched." testid="admin-payg-transition">
      {rows.length === 0 ? <p className="text-sm text-gray-400">No paid subscribers to move.</p> : (
        <table className="w-full text-sm"><thead><tr className="text-left text-xs text-gray-500"><th className="py-2">Agency</th><th>Old plan</th><th>MRR</th><th>Switch date</th><th>Status</th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.workspace_id} className="border-t border-white/[0.05] text-gray-300" data-testid={`payg-row-${r.workspace_id}`}>
              <td className="py-2 text-white max-w-[260px] truncate">{r.name || r.workspace_id}</td>
              <td className="capitalize">{r.payg_previous_plan || r.plan}</td><td>${Number(r.mrr || 0).toFixed(0)}</td>
              <td>{fmt(r.payg_switched_at || r.payg_switch_at)}</td><td>{STATE[r.payg_transition] || 'Not scheduled yet'}</td>
            </tr>))}</tbody></table>)}
    </Panel>
  );
};

export const EnterpriseLeadsPanel = () => {
  const { api } = useAuth();
  const [leads, setLeads] = useState(null);
  const load = () => api.get('/api/admin/enterprise-leads').then(r => setLeads(r.data.leads)).catch(() => setLeads([]));
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = async (id, status) => { await api.patch(`/api/admin/enterprise-leads/${id}`, { status }); load(); };
  if (!leads) return null;
  return (
    <Panel icon={Briefcase} title="Enterprise inquiries" sub="From the 'Talk to our team' form. You also get a bell notification for each new one." testid="admin-enterprise-leads">
      {leads.length === 0 ? <p className="text-sm text-gray-400">No inquiries yet.</p> : (
        <table className="w-full text-sm"><thead><tr className="text-left text-xs text-gray-500"><th className="py-2">Name</th><th>Contact</th><th>Clients</th><th>Use case</th><th>Heard from</th><th>Date</th><th>Status</th></tr></thead>
          <tbody>{leads.map(l => (
            <tr key={l.lead_id} className="border-t border-white/[0.05] text-gray-300 align-top" data-testid={`enterprise-lead-${l.lead_id}`}>
              <td className="py-2 text-white">{l.first_name} {l.last_name}</td>
              <td><div>{l.email}</div><div className="text-xs text-gray-500">{l.phone}</div></td>
              <td>{l.clients}</td><td className="max-w-[280px]"><div className="text-xs text-gray-500">{l.usage}</div>{l.use_case}</td>
              <td>{l.heard_from}</td><td>{fmt(l.created_at)}</td>
              <td><select value={l.status} onChange={e => setStatus(l.lead_id, e.target.value)} className="bg-transparent border border-white/10 rounded px-1 py-0.5 text-xs" data-testid={`enterprise-lead-status-${l.lead_id}`}>
                {['new', 'contacted', 'won', 'lost'].map(s => <option key={s} value={s}>{s}</option>)}</select></td>
            </tr>))}</tbody></table>)}
    </Panel>
  );
};
